import { Info } from 'lucide-react'
import type { Control } from 'react-hook-form'

import type { ConsentType } from '@/api/types'
import { CheckboxField } from '@/components/form/fields'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'

import { CONSENT_STATEMENTS, CONSENT_TEXT_VERSION } from '../constants'
import type { CustomerWizardInput } from '../validation'

type ConsentFieldName =
  | 'consentAccountOperation'
  | 'consentCreditBureau'
  | 'consentMarketing'
  | 'consentBiometricCapture'

const FIELD_NAMES: Record<ConsentType, ConsentFieldName> = {
  AccountOperation: 'consentAccountOperation',
  CreditBureau: 'consentCreditBureau',
  Marketing: 'consentMarketing',
  BiometricCapture: 'consentBiometricCapture',
}

/**
 * Consent capture.
 *
 * Each statement is shown in full rather than summarised, because the text stored against the
 * record is the text the customer was read, and the version recorded alongside it is what makes
 * that provable later. Marketing is genuinely optional and genuinely revocable; account operation
 * is not a choice so much as a record of the basis for holding the file at all.
 */
export function ConsentsStep({ control }: { control: Control<CustomerWizardInput> }) {
  return (
    <div className="space-y-4">
      <Alert tone="info">
        <Info aria-hidden />
        <div className="space-y-1">
          <AlertTitle>Read each statement to the customer</AlertTitle>
          <AlertDescription>
            What is ticked here is stored against version {CONSENT_TEXT_VERSION} of this wording,
            with your name and the time. Transactional alerts about the customer&apos;s own accounts
            do not depend on the marketing consent, and never will.
          </AlertDescription>
        </div>
      </Alert>

      {CONSENT_STATEMENTS.map((statement) => (
        <div
          key={statement.consentType}
          className="space-y-2 rounded-card border border-border-subtle bg-surface p-4"
        >
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-heading">{statement.title}</h3>
            <span className="text-2xs text-muted">
              {statement.mandatory ? 'Required' : 'Optional, and may be withdrawn later'}
            </span>
          </div>
          <p className="max-w-3xl text-sm text-body">{statement.text}</p>
          <CheckboxField
            control={control}
            name={FIELD_NAMES[statement.consentType]}
            label="The customer agrees to this"
          />
        </div>
      ))}
    </div>
  )
}
